import { useState } from 'react'
import {
  Headphones,
  Plus,
  AlertTriangle,
  CheckCircle2,
  Clock,
  MessageSquare,
  Tag,
  Search,
  ChevronRight,
} from 'lucide-react'
import styles from './ServiceDeskPage.module.css'

type Priority = 'critical' | 'high' | 'medium' | 'low'
type TicketStatus = 'open' | 'in_progress' | 'resolved' | 'closed'
type TicketType = 'incident' | 'request' | 'problem' | 'change'
type TabFilter = 'all' | 'open' | 'in_progress' | 'resolved' | 'closed'

interface Ticket {
  id: string
  title: string
  priority: Priority
  type: TicketType
  requester: string
  requesterInitials: string
  createdAt: string
  status: TicketStatus
  tags: string[]
  comments: number
}

const MOCK_TICKETS: Ticket[] = [
  { id: 'SDTK-1241', title: 'AppCentral production login failure for APAC region',          priority: 'critical', type: 'incident', requester: 'A. Patel',     requesterInitials: 'AP', createdAt: '2026-04-09 07:23', status: 'open',        tags: ['AppCentral', 'Auth'],    comments: 8  },
  { id: 'SDTK-1240', title: 'Pay EDI integration dropping transactions in demo',            priority: 'high',     type: 'incident', requester: 'J. Williams',  requesterInitials: 'JW', createdAt: '2026-04-09 06:12', status: 'in_progress', tags: ['Pay EDI', 'Integration'],comments: 5  },
  { id: 'SDTK-1239', title: 'Request: provision new UAT environment for M2M v4.3',         priority: 'medium',   type: 'request',  requester: 'S. Kumar',     requesterInitials: 'SK', createdAt: '2026-04-08 14:30', status: 'open',        tags: ['M2M', 'Provisioning'],   comments: 2  },
  { id: 'SDTK-1238', title: 'Monitoring alerts flooding Slack for pod restarts',            priority: 'high',     type: 'problem',  requester: 'M. Chen',      requesterInitials: 'MC', createdAt: '2026-04-08 11:05', status: 'in_progress', tags: ['Monitoring', 'AKS'],     comments: 11 },
  { id: 'SDTK-1237', title: 'GenAI service latency spike above SLA threshold',             priority: 'high',     type: 'incident', requester: 'R. Singh',     requesterInitials: 'RS', createdAt: '2026-04-08 09:50', status: 'open',        tags: ['GenAI', 'Performance'],  comments: 3  },
  { id: 'SDTK-1236', title: 'Change request: upgrade Kubernetes from 1.28 to 1.31',       priority: 'medium',   type: 'change',   requester: 'S. Kumar',     requesterInitials: 'SK', createdAt: '2026-04-07 16:00', status: 'open',        tags: ['Kubernetes', 'Upgrade'], comments: 7  },
  { id: 'SDTK-1235', title: 'Fixed Assets export generating corrupted CSV files',          priority: 'medium',   type: 'problem',  requester: 'L. Thompson',  requesterInitials: 'LT', createdAt: '2026-04-07 13:20', status: 'in_progress', tags: ['Fixed Assets', 'Bug'],   comments: 4  },
  { id: 'SDTK-1234', title: 'New user onboarding: 3 accounts for EMEA team',              priority: 'low',      type: 'request',  requester: 'P. Okonkwo',   requesterInitials: 'PO', createdAt: '2026-04-07 10:00', status: 'open',        tags: ['IAM', 'Onboarding'],     comments: 1  },
  { id: 'SDTK-1233', title: 'Certificate renewal for pay-edi.aptean.app failed',          priority: 'high',     type: 'incident', requester: 'M. Chen',      requesterInitials: 'MC', createdAt: '2026-04-06 08:30', status: 'resolved',    tags: ['Certificates', 'TLS'],   comments: 6  },
  { id: 'SDTK-1232', title: 'HAProxy config reload caused brief service interruption',     priority: 'medium',   type: 'incident', requester: 'A. Patel',     requesterInitials: 'AP', createdAt: '2026-04-05 20:15', status: 'closed',      tags: ['HAProxy', 'Network'],    comments: 9  },
  { id: 'SDTK-1231', title: 'Request: Agent Central sandbox for demo purposes',            priority: 'low',      type: 'request',  requester: 'D. Park',      requesterInitials: 'DP', createdAt: '2026-04-05 15:00', status: 'resolved',    tags: ['Agent Central'],         comments: 2  },
  { id: 'SDTK-1230', title: 'Terraform plan failing due to provider version mismatch',     priority: 'medium',   type: 'problem',  requester: 'J. Williams',  requesterInitials: 'JW', createdAt: '2026-04-04 11:30', status: 'closed',      tags: ['Terraform', 'IaC'],      comments: 3  },
]

const PRIORITY_CONFIG: Record<Priority, { label: string; cls: string }> = {
  critical: { label: 'Critical', cls: 'priCritical' },
  high:     { label: 'High',     cls: 'priHigh'     },
  medium:   { label: 'Medium',   cls: 'priMedium'   },
  low:      { label: 'Low',      cls: 'priLow'      },
}

const STATUS_CONFIG: Record<TicketStatus, { label: string; cls: string }> = {
  open:        { label: 'Open',        cls: 'statusOpen'       },
  in_progress: { label: 'In Progress', cls: 'statusInProgress' },
  resolved:    { label: 'Resolved',    cls: 'statusResolved'   },
  closed:      { label: 'Closed',      cls: 'statusClosed'     },
}

const TYPE_CONFIG: Record<TicketType, string> = {
  incident: 'typeIncident',
  request:  'typeRequest',
  problem:  'typeProblem',
  change:   'typeChange',
}

const TAB_FILTER_MAP: Record<TabFilter, TicketStatus[]> = {
  all:         ['open', 'in_progress', 'resolved', 'closed'],
  open:        ['open'],
  in_progress: ['in_progress'],
  resolved:    ['resolved'],
  closed:      ['closed'],
}

export function ServiceDeskPage() {
  const [activeTab, setActiveTab] = useState<TabFilter>('all')
  const [search, setSearch] = useState('')
  const [showModal, setShowModal] = useState(false)

  const filtered = MOCK_TICKETS.filter((t) => {
    const matchesTab = TAB_FILTER_MAP[activeTab].includes(t.status)
    const matchesSearch =
      t.title.toLowerCase().includes(search.toLowerCase()) ||
      t.id.toLowerCase().includes(search.toLowerCase()) ||
      t.requester.toLowerCase().includes(search.toLowerCase())
    return matchesTab && matchesSearch
  })

  const openCount       = MOCK_TICKETS.filter((t) => t.status === 'open').length
  const pendingCount    = MOCK_TICKETS.filter((t) => t.status === 'in_progress').length

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}>
            <Headphones size={20} />
          </div>
          <div>
            <h1 className={styles.pageTitle}>Service Desk</h1>
            <p className={styles.pageSubtitle}>Manage support tickets, incidents, and service requests</p>
          </div>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.btnPrimary} onClick={() => setShowModal(true)}>
            <Plus size={15} />
            Create Ticket
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'var(--dc-support-bg)', color: 'var(--dc-support)' }}>
            <Headphones size={18} />
          </div>
          <div>
            <div className={styles.statValue}>{openCount}</div>
            <div className={styles.statLabel}>Open Tickets</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'var(--aptean-warning-bg)', color: 'var(--aptean-warning)' }}>
            <Clock size={18} />
          </div>
          <div>
            <div className={styles.statValue}>{pendingCount}</div>
            <div className={styles.statLabel}>In Progress</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'var(--aptean-success-bg)', color: 'var(--aptean-success)' }}>
            <CheckCircle2 size={18} />
          </div>
          <div>
            <div className={styles.statValue}>96%</div>
            <div className={styles.statLabel}>SLA Met</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'var(--dc-deploy-bg)', color: 'var(--dc-deploy)' }}>
            <Clock size={18} />
          </div>
          <div>
            <div className={styles.statValue}>4.2h</div>
            <div className={styles.statLabel}>Avg Resolution</div>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className={styles.filterBar}>
        <div className={styles.searchWrapper}>
          <Search size={14} className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            placeholder="Search tickets..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className={styles.tabs}>
          {([
            { id: 'all'         as TabFilter, label: 'All',         count: MOCK_TICKETS.length },
            { id: 'open'        as TabFilter, label: 'Open',        count: openCount           },
            { id: 'in_progress' as TabFilter, label: 'In Progress', count: pendingCount        },
            { id: 'resolved'    as TabFilter, label: 'Resolved',    count: MOCK_TICKETS.filter((t) => t.status === 'resolved').length },
            { id: 'closed'      as TabFilter, label: 'Closed',      count: MOCK_TICKETS.filter((t) => t.status === 'closed').length  },
          ]).map((tab) => (
            <button
              key={tab.id}
              className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
              <span className={styles.tabBadge}>{tab.count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className={styles.tableCard}>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Title</th>
                <th>Priority</th>
                <th>Type</th>
                <th>Requester</th>
                <th>Created</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((ticket) => {
                const pri = PRIORITY_CONFIG[ticket.priority]
                const sts = STATUS_CONFIG[ticket.status]
                return (
                  <tr key={ticket.id} className={styles.tableRow}>
                    <td className={styles.cellId}>{ticket.id}</td>
                    <td>
                      <div className={styles.ticketTitle}>{ticket.title}</div>
                      <div className={styles.ticketTags}>
                        {ticket.tags.map((tag) => (
                          <span key={tag} className={styles.tag}>
                            <Tag size={9} />
                            {tag}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <span className={`${styles.priBadge} ${styles[pri.cls]}`}>
                        {ticket.priority === 'critical' && <AlertTriangle size={10} />}
                        {pri.label}
                      </span>
                    </td>
                    <td>
                      <span className={`${styles.typeBadge} ${styles[TYPE_CONFIG[ticket.type]]}`}>
                        {ticket.type}
                      </span>
                    </td>
                    <td>
                      <div className={styles.requesterCell}>
                        <span className={styles.requesterAvatar}>{ticket.requesterInitials}</span>
                        <span className={styles.requesterName}>{ticket.requester}</span>
                      </div>
                    </td>
                    <td className={styles.cellMuted}>{ticket.createdAt}</td>
                    <td>
                      <span className={`${styles.statusBadge} ${styles[sts.cls]}`}>
                        {ticket.status === 'open'        && <Clock size={10} />}
                        {ticket.status === 'in_progress' && <RefreshIcon />}
                        {ticket.status === 'resolved'    && <CheckCircle2 size={10} />}
                        {ticket.status === 'closed'      && <CheckCircle2 size={10} />}
                        {sts.label}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actionRow}>
                        {ticket.comments > 0 && (
                          <span className={styles.commentCount}>
                            <MessageSquare size={11} />
                            {ticket.comments}
                          </span>
                        )}
                        <button className={styles.actionBtn}>
                          <ChevronRight size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className={styles.emptyState}>
            <Headphones size={32} />
            <p>No tickets match your filters</p>
          </div>
        )}
      </div>

      {/* Create Ticket Modal */}
      {showModal && (
        <div className={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 className={styles.modalTitle}>Create Service Desk Ticket</h2>
            <p className={styles.modalSubtitle}>Describe your issue or request in detail</p>
            <div className={styles.formGrid}>
              <div className={styles.formGroupFull}>
                <label className={styles.formLabel}>Title</label>
                <input className={styles.formInput} placeholder="Brief description of the issue" />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Priority</label>
                <select className={styles.formSelect}>
                  <option>low</option>
                  <option>medium</option>
                  <option>high</option>
                  <option>critical</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Type</label>
                <select className={styles.formSelect}>
                  <option>incident</option>
                  <option>request</option>
                  <option>problem</option>
                  <option>change</option>
                </select>
              </div>
              <div className={styles.formGroupFull}>
                <label className={styles.formLabel}>Description</label>
                <textarea className={styles.formTextarea} rows={4} placeholder="Provide full details, steps to reproduce, and expected behavior..." />
              </div>
            </div>
            <div className={styles.modalActions}>
              <button className={styles.btnSecondary} onClick={() => setShowModal(false)}>Cancel</button>
              <button className={styles.btnPrimary} onClick={() => setShowModal(false)}>
                <Headphones size={14} />
                Submit Ticket
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function RefreshIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
    </svg>
  )
}
